/**
 * A list or a record inside an answer keeps the entries that parse, as `fetchRows` does for an
 * answer that is the list. A schema cannot reach the sink, so the parse in src/api/contract.ts
 * reports what was dropped, by its path. See docs/rules/api_boundary.md.
 */

import { z } from 'zod';

import type { DroppedRow, PathSegment, RowKeyReader } from '@/types/contract';

interface Collected<T> {
    result: T;
    droppedRows: DroppedRow[];
}

/**
 * The rows `listOf` schemas drop during one parse. Parsing is synchronous, so the rows of the
 * parse in progress are the top frame.
 */
class RowDrops {
    private readonly frames: DroppedRow[][] = [];

    collect<T>(parse: () => T): Collected<T> {
        const droppedRows: DroppedRow[] = [];
        this.frames.push(droppedRows);
        try {
            return {
                result: parse(),
                droppedRows,
            };
        } finally {
            this.frames.pop();
        }
    }

    add(row: DroppedRow): void {
        this.frames.at(-1)?.push(row);
    }
}

// One for the page: a schema cannot carry a collector of its own, and module scope may hold no
// mutable state, so the stack lives in this instance (docs/rules/data_flow.md).
export const rowDrops = new RowDrops();

/** A value's fields, where the value is an object. */
export const FieldsSchema = z.record(z.string(), z.unknown());

/** What a list or a record holds, which its transform hides from whoever walks the schema. */
export const entrySchemas = z.registry<{ kind: 'list' | 'record'; entry: unknown }>();

interface ListOptions {
    /** Names a row in a report; a row it cannot name is named by its index. */
    getKey?: RowKeyReader;
}

/** Reads a row's key from its `field`, a string or a number; null where the row has none. */
export function keyBy(field: string): RowKeyReader {
    return (row: unknown): string | null => {
        const fields = FieldsSchema.safeParse(row);
        const key = fields.success ? fields.data[field] : undefined;
        switch (typeof key) {
            case 'string':
                return key === '' ? null : key;
            case 'number':
                return String(key);
            default:
                return null;
        }
    };
}

/**
 * One entry, parsed. The rows its own lists dropped go up under `place`; an entry that does not
 * parse is dropped whole, its inner rows with it.
 */
function getEntry<S extends z.ZodType>(
    schema: S, rawValue: unknown, place: PathSegment[],
): z.ZodSafeParseResult<z.output<S>> {
    const { result, droppedRows } = rowDrops.collect(() => schema.safeParse(rawValue));
    if (!result.success) {
        rowDrops.add({
            path: place,
            issues: result.error.issues,
        });
        return result;
    }
    for (const row of droppedRows) {
        rowDrops.add({
            path: [...place, ...row.path],
            issues: row.issues,
        });
    }
    return result;
}

/**
 * Only a value that is no list at all breaks it; a row that does not parse is dropped. `name` is
 * the field the list sits in, so the report says which list of the answer lost a row. A list
 * that is a record's value has no field of its own: `name` is then what it holds, as
 * `DeviceSuggestionsSchema` names its lists `suggestions`.
 */
export function listOf<S extends z.ZodType>(
    itemSchema: S, name: string, { getKey }: ListOptions = {},
): z.ZodPipe<z.ZodArray<z.ZodUnknown>, z.ZodTransform<z.output<S>[], unknown[]>> {
    const schema = z.array(z.unknown()).transform((rows): z.output<S>[] => (
        rows.flatMap((row, index): z.output<S>[] => {
            const result = getEntry(itemSchema, row, [name, getKey?.(row) ?? index]);
            return result.success ? [result.data] : [];
        })
    ));
    entrySchemas.add(schema, {
        kind: 'list',
        entry: itemSchema,
    });
    return schema;
}

interface RecordOptions {
    /** The field the record sits in, none for a record that is the answer. */
    name?: string;
    /** What a key must be; an entry whose key it refuses is dropped, reported by that key. */
    keySchema?: z.ZodType<string>;
}

/** Only a value that is no record breaks it; an entry that does not parse is dropped. */
export function recordOf<S extends z.ZodType>(
    valueSchema: S, { name, keySchema }: RecordOptions = {},
): z.ZodPipe<
    z.ZodRecord<z.ZodString, z.ZodUnknown>,
    z.ZodTransform<Record<string, z.output<S>>, Record<string, unknown>>
> {
    const schema = FieldsSchema.transform(
        (entries): Record<string, z.output<S>> => (
            Object.fromEntries(Object.entries(entries).flatMap(
                ([key, value]): [string, z.output<S>][] => {
                    const place = name === undefined ? [key] : [name, key];
                    const keyResult = keySchema?.safeParse(key);
                    if (keyResult?.success === false) {
                        rowDrops.add({
                            path: place,
                            issues: keyResult.error.issues,
                        });
                        return [];
                    }
                    const result = getEntry(valueSchema, value, place);
                    return result.success ? [[key, result.data]] : [];
                },
            ))
        ),
    );
    entrySchemas.add(schema, {
        kind: 'record',
        entry: valueSchema,
    });
    return schema;
}
